import { createContext, type ReactNode, use, useEffect, useSyncExternalStore } from 'react';
import { createJourneyClient, type EntryParams, type JourneyClient, type JourneyConfig } from './journeyClient';
import type { JourneySnapshot } from './schema';

export interface JourneyContextValue<T> {
  snapshot: JourneySnapshot<T> | null;
  capture: (params: EntryParams) => JourneySnapshot<T> | null;
  clear: () => void;
}

/**
 * Builds a journey instance from a config: the persistence client plus a typed
 * Provider and hook bound to it. Call once per journey type (booking, ...); the
 * consumer owns the payload schema and its entry-param mapping.
 *
 * Mount the Provider above the auth boundary so a public entry landing captures
 * and authenticated steps read the same snapshot.
 */
export function createJourney<T>(config: JourneyConfig<T>): {
  client: JourneyClient<T>;
  JourneyProvider: (props: { children: ReactNode }) => ReactNode;
  useJourney: () => JourneyContextValue<T>;
} {
  const client = createJourneyClient(config);
  const JourneyContext = createContext<JourneyContextValue<T> | null>(null);

  function JourneyProvider({ children }: { children: ReactNode }) {
    // The client is the store, not React state: a capture from outside the tree (a route loader,
    // another tab) has to reach consumers too.
    const snapshot = useSyncExternalStore(client.subscribe, client.peek, client.peek);

    // Reaping writes, so it stays out of render. `peek` already refuses an expired entry, this
    // only keeps storage from carrying it around.
    useEffect(() => {
      client.read();
    }, []);

    return <JourneyContext value={{ snapshot, capture: client.capture, clear: client.clear }}>{children}</JourneyContext>;
  }

  function useJourney(): JourneyContextValue<T> {
    const value = use(JourneyContext);
    if (value === null) {
      throw new Error('useJourney must be used within its JourneyProvider');
    }
    return value;
  }

  return { client, JourneyProvider, useJourney };
}
